import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trophy, Search, Calendar, Award, ChevronRight, Plus, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { tournamentAPI } from '../services/tournamentApi';
import { transformTournamentData } from '../utils/tournamentHelpers';
import BottomSheet from '../../components/common/BottomSheet';

const MAX_TOURNAMENTS = 3;
const MAX_COMPLETED = 3;
const ACTIVE_STATUSES = ['In Progress', 'Active'];
const EXCLUDED_DISCOVERY_STATUSES = ['Draft', 'Cancelled', 'Completed'];

/** Mobile bottom-sheet quick-nav for Tournaments — tournaments the user is organizing or
 *  playing in surface first; if they have none, falls back to active tournaments (then
 *  upcoming, soonest first) so the panel still has something useful to show. */
const TournamentsQuickPanel = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDiscovery, setIsDiscovery] = useState(false);
  const [completedTournaments, setCompletedTournaments] = useState([]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const involvedResponse = await tournamentAPI.getAll({ involvedUserId: user.id, excludeCompleted: true });
      const involved = (involvedResponse?.data || [])
        .map(transformTournamentData)
        .sort((a, b) => ACTIVE_STATUSES.includes(b.status) - ACTIVE_STATUSES.includes(a.status));

      if (involved.length > 0) {
        setIsDiscovery(false);
        setTournaments(involved.slice(0, MAX_TOURNAMENTS));
      } else {
        // No involvement anywhere — fall back to what's happening: active tournaments first,
        // then upcoming ones soonest-first, so the panel isn't just empty.
        const allResponse = await tournamentAPI.getAll({ excludeCompleted: true });
        const discovery = (allResponse?.data || [])
          .map(transformTournamentData)
          .filter(t => !EXCLUDED_DISCOVERY_STATUSES.includes(t.status))
          .sort((a, b) => {
            const activeDiff = ACTIVE_STATUSES.includes(b.status) - ACTIVE_STATUSES.includes(a.status);
            if (activeDiff !== 0) return activeDiff;
            return new Date(a.startDateRaw || 0) - new Date(b.startDateRaw || 0);
          });
        setIsDiscovery(true);
        setTournaments(discovery.slice(0, MAX_TOURNAMENTS));
      }

      // Most recently completed tournaments the user played in — independent of the
      // upcoming/active list above, shown as its own compact section.
      const completedResponse = await tournamentAPI.getAll({
        involvedUserId: user.id,
        status: 'completed',
        limit: MAX_COMPLETED
      });
      setCompletedTournaments((completedResponse?.data || []).map(transformTournamentData).slice(0, MAX_COMPLETED));
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const goToTournament = (id, tab) => {
    onClose();
    navigate(`/tournament/${id}${tab ? `?tab=${tab}` : ''}`);
  };

  const goToList = () => {
    onClose();
    navigate('/tournaments');
  };

  const goToCreate = () => {
    onClose();
    navigate('/create-tournament');
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <Trophy className={`w-5 h-5 ${theme.primary}`} />
            <span className={`font-semibold ${theme.text}`}>{isDiscovery ? 'Tournaments Happening Now' : 'Your Tournaments'}</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={goToCreate} className={`p-1.5 rounded-lg ${theme.cardHover}`} title="Create Tournament">
              <Plus className={`w-4 h-4 ${theme.primary}`} />
            </button>
            <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.cardHover}`}>
              <X className={`w-4 h-4 ${theme.textMuted}`} />
            </button>
          </div>
        </div>

        <div className={`h-px ${theme.border} bg-current opacity-10 mx-4`} />

        <div className="px-4 py-3 overflow-y-auto" style={{ maxHeight: '70vh' }}>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className={`w-6 h-6 border-2 border-t-transparent rounded-full animate-spin ${theme.primary.replace('text-', 'border-')}`} />
            </div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className={`text-sm ${theme.textMuted} mb-3`}>No tournaments to show right now</p>
            </div>
          ) : (
            <div className="space-y-3 mb-3">
              {tournaments.map(t => (
                <div key={t.id} className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-3`}>
                  <button
                    onClick={() => goToTournament(t.id)}
                    className="flex items-center justify-between w-full mb-2 text-left"
                  >
                    <span className={`font-semibold text-sm ${theme.text} truncate`}>{t.emoji} {t.name}</span>
                    <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                      ACTIVE_STATUSES.includes(t.status)
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    }`}>
                      {t.status}
                    </span>
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => goToTournament(t.id, 'smart_schedule')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium ${theme.cardHover} ${theme.border} ${theme.textSecondary}`}
                    >
                      <Calendar className="w-3.5 h-3.5" /> Schedule
                    </button>
                    <button
                      onClick={() => goToTournament(t.id, 'standings')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium ${theme.cardHover} ${theme.border} ${theme.textSecondary}`}
                    >
                      <Award className="w-3.5 h-3.5" /> Standings
                    </button>
                    <button
                      onClick={() => goToTournament(t.id, 'register')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium ${theme.cardHover} ${theme.border} ${theme.textSecondary}`}
                    >
                      <Trophy className="w-3.5 h-3.5" /> Register
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && completedTournaments.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 px-1 mb-2">
                <CheckCircle2 className={`w-3.5 h-3.5 ${theme.textMuted}`} />
                <span className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted}`}>Recently Completed</span>
              </div>
              <div className="space-y-2">
                {completedTournaments.map(t => (
                  <button
                    key={t.id}
                    onClick={() => goToTournament(t.id, 'standings')}
                    className={`flex items-center justify-between w-full rounded-xl border ${theme.border} ${theme.backgroundSecondary} px-3 py-2 text-left`}
                  >
                    <span className="min-w-0">
                      <span className={`block font-medium text-sm ${theme.text} truncate`}>{t.emoji} {t.name}</span>
                      <span className={`block text-xs ${theme.textMuted}`}>{t.startDate}</span>
                    </span>
                    <span className={`shrink-0 flex items-center gap-1 text-xs font-medium ${theme.textSecondary}`}>
                      <Award className="w-3.5 h-3.5" /> Standings
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={goToList}
            className={`flex items-center justify-between w-full py-2.5 px-3 rounded-lg ${theme.cardHover} ${theme.textSecondary} text-sm font-medium`}
          >
            <span className="flex items-center gap-2"><Search className="w-4 h-4" /> Browse all tournaments</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
    </BottomSheet>
  );
};

export default TournamentsQuickPanel;
